import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import SwitchAccountOutlinedIcon from '@mui/icons-material/SwitchAccountOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';
import { acceptEditorInvitation, declineEditorInvitation } from '../graphqlService';
import AuthLayout from './Auth/AuthLayout';
import AuthStatus, { AuthStatusAction, AuthStatusTone } from './Auth/AuthStatus';
import { isNetworkFailure, NETWORK_ERROR_MESSAGE } from './Auth/authErrors';
import { SUPPORT_HREF } from './Auth/authForm';
import { checkInvitationLink, INVITATION_PROBLEM_COPY, InvitationLinkProblem } from './Auth/authLinks';
import KeyValueList from './ui/KeyValueList';
import TermsAndConditions from './Legal/TermsAndConditions';
import { EDITOR_HOME_PATH } from '../utils/editorAccount';
import { brand } from '../theme';

/** What the accept / decline mutations answer. */
interface InvitationResponse {
  success: boolean;
  message?: string | null;
  redirectTo?: string | null;
  error?: string | null;
}

type Outcome =
  | { kind: 'accepted'; redirectTo: string; redirecting: boolean }
  | { kind: 'sign-in-required'; message: string; redirectTo: string }
  | { kind: 'wrong-account'; message: string }
  | { kind: 'already-accepted'; redirectTo: string; viaDecline: boolean }
  | { kind: 'declined'; redirectTo: string; redirecting: boolean }
  | { kind: 'link-problem'; problem: InvitationLinkProblem | 'withdrawn' }
  | { kind: 'failed'; action: 'accept' | 'decline'; network: boolean };

const WITHDRAWN_COPY = {
  title: 'This invitation is no longer available',
  description: 'The creator may have withdrawn it. Ask them to send a new invitation.',
};

/**
 * The API's answer when it refuses, mapped by its error code. Its raw error
 * text (a server exception message for unexpected failures) never shows.
 */
const outcomeForRefusal = (
  response: InvitationResponse,
  action: 'accept' | 'decline',
  email: string
): Outcome => {
  switch (response.error) {
    case 'SIGN_IN_REQUIRED':
      return {
        kind: 'sign-in-required',
        message: response.message || `You already have a PithPlay account. Sign in as ${email} to accept this invitation.`,
        redirectTo: response.redirectTo || '/login',
      };
    case 'WRONG_ACCOUNT':
      return {
        kind: 'wrong-account',
        message: response.message || `This invitation is for ${email}. Sign out, then sign in as ${email} to accept it.`,
      };
    case 'Already accepted':
      return { kind: 'already-accepted', redirectTo: response.redirectTo || EDITOR_HOME_PATH, viaDecline: action === 'decline' };
    case 'Invalid token':
    case 'Wrong token type':
    case 'Invalid token structure':
      return { kind: 'link-problem', problem: 'invalid' };
    case 'Token expired':
      return { kind: 'link-problem', problem: 'expired' };
    case 'Email mismatch':
      return { kind: 'link-problem', problem: 'wrong-email' };
    case 'Invitation not found':
      return { kind: 'link-problem', problem: 'withdrawn' };
    default:
      return { kind: 'failed', action, network: false };
  }
};

const EditorInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Tracks pending navigation timers so they can be cancelled on unmount.
  // Without this a timer could call navigate() after the component was gone,
  // and StrictMode's double-mount scheduled two of them.
  const pendingTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => () => {
    pendingTimersRef.current.forEach(clearTimeout);
    pendingTimersRef.current = [];
  }, []);
  const redirectAfter = (to: string, ms: number) => {
    pendingTimersRef.current.push(setTimeout(() => navigate(to), ms));
  };

  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const linkProblem = useMemo(() => checkInvitationLink(token, email), [token, email]);

  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [showTerms, setShowTerms] = useState(false);

  const handleAccept = async () => {
    if (linkProblem || busy || !token || !email) return;
    setBusy('accept');
    try {
      const response: InvitationResponse = await acceptEditorInvitation(token, email);
      if (response.success) {
        const redirecting = Boolean(response.redirectTo);
        setOutcome({ kind: 'accepted', redirectTo: response.redirectTo || EDITOR_HOME_PATH, redirecting });
        if (response.redirectTo) redirectAfter(response.redirectTo, 3000);
      } else {
        setOutcome(outcomeForRefusal(response, 'accept', email));
        // An existing account can only be linked by its owner. Send them to
        // sign in; Login returns them here to finish accepting.
        if (response.error === 'SIGN_IN_REQUIRED' && response.redirectTo) {
          redirectAfter(response.redirectTo, 2000);
        }
      }
    } catch (error: unknown) {
      console.error('Failed to accept editor invitation:', error);
      setOutcome({ kind: 'failed', action: 'accept', network: isNetworkFailure(error) });
    } finally {
      setBusy(null);
    }
  };

  const handleDecline = async () => {
    if (linkProblem || busy || !token || !email) return;
    setBusy('decline');
    try {
      const response: InvitationResponse = await declineEditorInvitation(token, email);
      if (response.success) {
        const redirecting = Boolean(response.redirectTo);
        setOutcome({ kind: 'declined', redirectTo: response.redirectTo || '/', redirecting });
        if (response.redirectTo) redirectAfter(response.redirectTo, 3000);
      } else {
        setOutcome(outcomeForRefusal(response, 'decline', email));
      }
    } catch (error: unknown) {
      console.error('Failed to decline editor invitation:', error);
      setOutcome({ kind: 'failed', action: 'decline', network: isNetworkFailure(error) });
    } finally {
      setBusy(null);
    }
  };

  const goToSignIn: AuthStatusAction = { label: 'Go to sign in', to: '/login' };
  const contactSupport: AuthStatusAction = { label: 'Contact support', href: SUPPORT_HREF };

  const renderStatus = (props: {
    tone: AuthStatusTone;
    icon?: React.ReactNode;
    title: string;
    description: string;
    primary: AuthStatusAction;
    secondary?: AuthStatusAction;
    focus?: boolean;
  }) => (
    <AuthLayout>
      <AuthStatus
        tone={props.tone}
        icon={props.icon}
        title={props.title}
        description={props.description}
        primaryAction={props.primary}
        secondaryAction={props.secondary}
        focusOnMount={props.focus}
      />
    </AuthLayout>
  );

  if (linkProblem) {
    const copy = INVITATION_PROBLEM_COPY[linkProblem];
    return renderStatus({
      tone: 'error',
      icon: <LinkOffOutlinedIcon />,
      title: copy.title,
      description: copy.description,
      primary: goToSignIn,
      secondary: contactSupport,
    });
  }

  if (outcome) {
    switch (outcome.kind) {
      case 'accepted': {
        const nextIsRegistration = outcome.redirectTo.startsWith('/editor-registration');
        return renderStatus({
          tone: 'success',
          title: 'Invitation accepted',
          description: nextIsRegistration
            ? 'Next, set up your editor account. Taking you there now…'
            : outcome.redirecting
              ? 'You now have editor access. Taking you to your projects…'
              : 'You now have editor access.',
          primary: {
            label: nextIsRegistration ? 'Set up your account' : 'Go to projects',
            to: outcome.redirectTo,
          },
          focus: true,
        });
      }
      case 'sign-in-required':
        return renderStatus({
          tone: 'info',
          icon: <LoginOutlinedIcon />,
          title: 'Sign in to accept',
          description: outcome.message,
          primary: { label: 'Sign in', to: outcome.redirectTo },
          focus: true,
        });
      case 'wrong-account':
        return renderStatus({
          tone: 'warning',
          icon: <SwitchAccountOutlinedIcon />,
          title: "You're signed in to a different account",
          description: outcome.message,
          // Signs out, then signs in and comes back here to accept.
          primary: {
            label: 'Sign out',
            to: `/logout?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
          },
          secondary: { label: 'Go to dashboard', to: '/dashboard' },
          focus: true,
        });
      case 'already-accepted':
        return renderStatus({
          tone: 'info',
          title: "You've already accepted this invitation",
          description: outcome.viaDecline
            ? 'To stop working with this creator, ask them to remove you from their editors.'
            : 'Your editor projects are ready for you.',
          primary: { label: 'Go to projects', to: outcome.redirectTo },
          focus: true,
        });
      case 'declined':
        return renderStatus({
          tone: 'neutral',
          icon: <DoNotDisturbOnOutlinedIcon />,
          title: 'Invitation declined',
          description: outcome.redirecting
            ? 'Thanks for letting us know. Taking you to PithPlay…'
            : 'Thanks for letting us know.',
          primary: { label: 'Go to PithPlay', to: outcome.redirectTo },
          focus: true,
        });
      case 'link-problem': {
        const copy = outcome.problem === 'withdrawn' ? WITHDRAWN_COPY : INVITATION_PROBLEM_COPY[outcome.problem];
        return renderStatus({
          tone: 'error',
          icon: <LinkOffOutlinedIcon />,
          title: copy.title,
          description: copy.description,
          primary: goToSignIn,
          secondary: contactSupport,
          focus: true,
        });
      }
      case 'failed':
        return renderStatus({
          tone: 'error',
          title: outcome.action === 'accept' ? "We couldn't accept the invitation" : "We couldn't decline the invitation",
          description: outcome.network ? NETWORK_ERROR_MESSAGE : 'Something went wrong on our side. Try again in a moment.',
          primary: {
            label: 'Try again',
            onClick: outcome.action === 'accept' ? handleAccept : handleDecline,
            loading: busy !== null,
            loadingLabel: 'Trying again…',
          },
          secondary: { label: 'Back to the invitation', onClick: () => setOutcome(null) },
          focus: true,
        });
      default:
        break;
    }
  }

  return (
    <AuthLayout title="Editor invitation" subtitle="You've been invited to edit video projects on PithPlay.">
      <Box
        sx={{
          border: `1px solid ${brand.border}`,
          borderRadius: 3,
          backgroundColor: 'background.paper',
          p: 2.5,
          mb: 3,
        }}
      >
        <KeyValueList
          columns={1}
          items={[
            { label: 'Invited email', value: email },
            { label: 'Role', value: 'Editor' },
          ]}
        />
      </Box>

      <Button fullWidth variant="contained" size="large" onClick={handleAccept} disabled={busy !== null}>
        {busy === 'accept' ? (
          <>
            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} aria-hidden="true" />
            Accepting…
          </>
        ) : (
          'Accept invitation'
        )}
      </Button>

      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
        <Button variant="text" onClick={handleDecline} disabled={busy !== null} sx={{ color: 'text.secondary' }}>
          {busy === 'decline' ? 'Declining…' : 'Decline'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        By accepting, you agree to the{' '}
        <Link component="button" type="button" variant="body2" onClick={() => setShowTerms(true)} sx={{ fontWeight: 600, verticalAlign: 'baseline' }}>
          Editor Terms and Conditions
        </Link>
        .
      </Typography>

      <TermsAndConditions
        open={showTerms}
        onClose={() => setShowTerms(false)}
        termsFile="EDITOR_TERMS_AND_CONDITIONS.md"
      />
    </AuthLayout>
  );
};

export default EditorInvitation;
